import { z } from 'zod';
import { zfd } from 'zod-form-data';
import {
  DraftClaimLineItem,
  draftClaimLineItemContext,
} from '~/app/features/crew/claim-line-items/claimLineItemMiddleware';
import {
  CrewClaim,
  crewClaimContext,
} from '~/app/features/crew/crewClaimMiddleware';
import { settingsContext } from '~/app/settingsMiddleware';
import api from '~/utils/api';
import { getClaimReasons } from '~/utils/claimLineItem';
import { makeAction, makeLoader, redirect } from '~/utils/routing';

const paramsSchema = z.object({
  reasonId: z.coerce.number().int().positive(),
});

const getReason = ({
  claimType,
  draftClaimLineItem,
}: {
  claimType: CrewClaim['claimType'];
  draftClaimLineItem: DraftClaimLineItem;
}) => {
  const claimReasons = getClaimReasons(claimType, draftClaimLineItem);
  const reason = claimReasons.find(
    ({ id }) => id === draftClaimLineItem.reasonId,
  );

  if (!reason) {
    throw new Error('Invalid claim reason.');
  }

  return reason;
};

export const { loader, useLoaderData } = makeLoader(({ params, context }) => {
  const { claimType } = context.get(crewClaimContext);
  const draftClaimLineItem = context.get(draftClaimLineItemContext);
  const { reasonId } = paramsSchema.parse(params);
  const reason = getReason({ claimType, draftClaimLineItem });

  if (reason.id !== reasonId) {
    throw new Error(
      'Invalid reason ID: does not match any claim reasons for claim type',
    );
  }

  // if there are no detail reasons, skip this step
  if (!reason.claimReasonDetails.length) {
    return redirect('../follow-up');
  }

  const reasonDetailOptions = reason.claimReasonDetails.map(({ id, name }) => ({
    name,
    id,
  }));

  return { reasonDetailOptions, draftClaimLineItem };
});

export const INPUT_NAME = 'reasonDetailId';

const makeFormSchema = (reason: ReturnType<typeof getClaimReasons>[number]) => {
  const ids = reason.claimReasonDetails.map(({ id }) => id);

  return zfd.formData({
    [INPUT_NAME]: zfd.numeric(
      z
        .number({ message: 'Please choose a reason.' })
        .int({ message: 'Invalid reason ID.' })
        .positive({ message: 'Invalid reason ID.' })
        .refine((id) => ids.includes(id), { message: 'Invalid reason ID.' }),
    ),
  });
};

export const { action, useActionResult } = makeAction(
  async ({ context, formData }, appStore) => {
    const { storefrontId } = context.get(settingsContext);
    const { claimType } = context.get(crewClaimContext);
    const draftClaimLineItem = context.get(draftClaimLineItemContext);
    const reason = getReason({ claimType, draftClaimLineItem });

    const result = makeFormSchema(reason).safeParse(formData);

    if (!result.success) {
      return {
        ok: false,
        message: result.error.message,
      };
    }

    const { reasonDetailId } = result.data;

    appStore.dispatch(
      appStore.actions.draftClaimLineItem.setReasonDetail(reasonDetailId),
    );

    const {
      eventCustomFields,
      eventPolicyEnforcement,
      omittedResolutionMethods,
    } = await api.calculateAfterReasonSelection({
      params: { storefrontId },
      body: {
        id: draftClaimLineItem.lineItem.id,
        claimType,
        calculatedLineItems: [
          {
            originalStoreOrderLineItemId: draftClaimLineItem.lineItem.id,
            quantity: draftClaimLineItem.quantity,
            grams: draftClaimLineItem.grams,
            unitPrice: draftClaimLineItem.unitPrice,
            unitTax: draftClaimLineItem.unitTax,
            comments: draftClaimLineItem.comments,
            reasonId: reason.id,
            reasonDetailId,
            reasonCategoryCode: reason.category.code,
            advancedExchangeMethod: draftClaimLineItem.advancedExchangeMethod,
          },
        ],
      },
    });

    appStore.dispatch(
      appStore.actions.draftClaimLineItem.setReasonPolicy(
        eventPolicyEnforcement,
      ),
    );

    appStore.dispatch(
      appStore.actions.draftClaimLineItem.setReasonCustomFields(
        eventCustomFields,
      ),
    );

    appStore.dispatch(
      appStore.actions.draftClaimLineItem.setOmittedResolutionMethods(
        omittedResolutionMethods,
      ),
    );

    return eventPolicyEnforcement
      ? redirect('../follow-up')
      : redirect('../meta');
  },
);
